import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiPlus, FiFolder, FiClock, FiTrendingUp, FiUser, FiLogOut, FiAward, FiLock } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import SubscriptionModal from './SubscriptionModal';

const Dashboard = ({ onCreateProject, onOpenProject }) => {
  const { user, logout, subscription, isProfessional } = useAuth();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [projects] = useState([
    {
      id: '1',
      name: 'Santos Makati Residence',
      location: 'Makati City, Metro Manila',
      area: '45.50 sqm',
      lastModified: '2 hours ago',
      status: 'In Progress',
      type: 'Residential'
    }
  ]);

  const stats = [
    { label: 'Active Projects', value: projects.length, icon: FiFolder, color: 'bg-blue-500' },
    { label: 'Completed Specs', value: '12', icon: FiTrendingUp, color: 'bg-green-500' },
    { label: 'Hours Saved', value: '48', icon: FiClock, color: 'bg-purple-500' }
  ];

  const handleCreateProject = () => {
    if (!isProfessional && projects.length >= 1) {
      setShowSubscriptionModal(true);
      return;
    }
    onCreateProject();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <motion.header 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white border-b border-gray-200 px-4 py-4"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">BQ</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">BQ SyncSpec</h1>
              <p className="text-sm text-gray-500">Construction Specifications</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {!isProfessional && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowSubscriptionModal(true)}
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-xl font-medium text-sm shadow-lg"
              >
                <SafeIcon icon={FiAward} size={16} />
                <span>Upgrade to Pro</span>
              </motion.button>
            )}
            
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200">
                <img 
                  src={user?.avatar} 
                  alt={user?.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  {isProfessional ? (
                    <>
                      <SafeIcon icon={FiAward} className="text-yellow-500" size={12} />
                      Professional
                    </>
                  ) : (
                    'Free Plan'
                  )}
                </p>
              </div>
              <button
                onClick={logout}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <SafeIcon icon={FiLogOut} size={18} />
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Welcome Section */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-gradient-to-r from-primary to-primary-dark rounded-2xl p-6 text-white"
        >
          <h2 className="text-2xl font-bold mb-2">Welcome back, {user?.name?.split(' ')[0]}!</h2>
          <p className="text-primary-light opacity-90">
            Ready to create professional ceiling assembly specifications?
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                  <SafeIcon icon={stat.icon} className="text-white" size={24} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Projects Section */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Your Projects</h3>
                <p className="text-sm text-gray-500">
                  {isProfessional ? 'Unlimited projects' : `${projects.length}/1 project used`}
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCreateProject}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl font-medium hover:bg-primary-dark transition-colors"
              >
                <SafeIcon icon={FiPlus} size={18} />
                <span className="hidden sm:inline">New Project</span>
              </motion.button>
            </div>
          </div>

          {projects.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiFolder} className="text-gray-400" size={32} />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">No projects yet</h4>
              <p className="text-gray-500 mb-6">Create your first ceiling assembly specification</p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCreateProject}
                className="px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary-dark transition-colors"
              >
                Create Your First Project
              </motion.button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {projects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ backgroundColor: '#f8fafc' }}
                  className="p-6 cursor-pointer transition-colors"
                  onClick={() => onOpenProject(project)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">{project.name}</h4>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{project.location}</span>
                        <span>•</span>
                        <span>{project.area}</span>
                        <span>•</span>
                        <span>{project.type}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          project.status === 'In Progress' 
                            ? 'bg-yellow-100 text-yellow-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {project.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">{project.lastModified}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {!isProfessional && projects.length >= 1 && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-t border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiLock} className="text-yellow-600" size={20} />
                  <div>
                    <p className="font-medium text-gray-900">Project limit reached</p>
                    <p className="text-sm text-gray-600">Upgrade to Professional to create unlimited projects</p>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowSubscriptionModal(true)}
                  className="px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-xl font-medium text-sm"
                >
                  Upgrade Now
                </motion.button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <SubscriptionModal 
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
      />
    </div>
  );
};

export default Dashboard;